import type {
  SearchFacetExtras,
  SearchFacetMember,
  SearchQuestionEcho,
  SearchResultItem,
} from '../../types';
import {
  DEAKIN_RESULT_META,
  deakinTopicLabel,
} from '../../funnelback/deakin-config';
import { decodeHtmlEntitiesPlain, resolveResultImageUrl } from '../../utils';

type UnknownRecord = Record<string, unknown>;

function asRecord(v: unknown): UnknownRecord | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as UnknownRecord)
    : null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function firstStringFromMeta(meta: unknown, keys: readonly string[]): string {
  const o = asRecord(meta);
  if (!o) {
    return '';
  }
  for (const k of keys) {
    const val = o[k];
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
      return val[0];
    }
    if (typeof val === 'string') {
      return val;
    }
  }
  return '';
}

/** Published date from listMetadata (e.g. `2025-08-25 13:14:08`). */
function publishedDateFromMeta(meta: UnknownRecord | null): string {
  return firstStringFromMeta(meta, DEAKIN_RESULT_META.publishedDate);
}

function isMatrixApiPlaceholderUrl(url: string): boolean {
  return (
    url.includes('matrix.squiz.cloud') &&
    (url.includes('/api/') || url.includes('/designs/api/'))
  );
}

function pickResultUrl(row: UnknownRecord, metaObj: UnknownRecord | null): string {
  const metaUrl = metaObj
    ? firstStringFromMeta(metaObj, DEAKIN_RESULT_META.url)
    : '';
  if (
    metaUrl &&
    !isMatrixApiPlaceholderUrl(metaUrl) &&
    (/^https?:\/\//i.test(metaUrl) || metaUrl.startsWith('/'))
  ) {
    return metaUrl;
  }

  const liveUrls = row.liveUrls;
  const firstLiveUrl =
    Array.isArray(liveUrls) && typeof liveUrls[0] === 'string'
      ? liveUrls[0]
      : '';
  const candidates = [
    asString(row.liveUrl ?? firstLiveUrl ?? ''),
    asString(row.indexUrl ?? ''),
    asString(row.displayUrl ?? ''),
  ];
  for (const candidate of candidates) {
    if (
      candidate &&
      !isMatrixApiPlaceholderUrl(candidate) &&
      (/^https?:\/\//i.test(candidate) || candidate.startsWith('/'))
    ) {
      return candidate;
    }
  }

  const click = asString(row.clickTrackingUrl ?? '');
  if (click.startsWith('/')) {
    return click;
  }
  return asString(row.url ?? row.U ?? '#') || '#';
}

function mapResultRow(raw: unknown): SearchResultItem | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  const metaObj = asRecord(row.listMetadata);

  const title =
    firstStringFromMeta(metaObj, DEAKIN_RESULT_META.title) ||
    asString(row.title ?? row.T);
  if (!title) {
    return null;
  }

  const topicKey = firstStringFromMeta(metaObj, DEAKIN_RESULT_META.topic);
  const typeLabel = topicKey ? deakinTopicLabel(topicKey) : '';

  let summary = firstStringFromMeta(metaObj, DEAKIN_RESULT_META.summary);
  if (!summary.trim()) {
    summary = asString(row.summary ?? row.S ?? row.cacheBlur ?? '');
  }
  summary = decodeHtmlEntitiesPlain(summary);

  const publishedDate = publishedDateFromMeta(metaObj);

  const url = pickResultUrl(row, metaObj);

  const thumbnailRaw = firstStringFromMeta(metaObj, DEAKIN_RESULT_META.image);
  const thumbnailUrl = resolveResultImageUrl(thumbnailRaw);

  const item: SearchResultItem = {
    typeLabel,
    title,
    metadata: '',
    summary,
    url,
  };
  if (publishedDate) {
    item.publishedDate = publishedDate;
  }
  if (thumbnailUrl) {
    item.thumbnailUrl = thumbnailUrl;
  }
  return item;
}

function mapFacetCategories(rawCategories: unknown): SearchFacetMember[] {
  if (!Array.isArray(rawCategories)) {
    return [];
  }
  const out: SearchFacetMember[] = [];
  for (const cat of rawCategories) {
    const c = asRecord(cat);
    if (!c) continue;
    const data = asString(c.data ?? c.query ?? '');
    const label = asString(c.label ?? c.data ?? data);
    const value = data || label;
    if (!value) continue;
    const countRaw = c.count ?? c.hits;
    const count =
      typeof countRaw === 'number'
        ? countRaw
        : typeof countRaw === 'string'
          ? Number.parseInt(countRaw, 10)
          : undefined;
    out.push({
      value,
      label: label || value,
      count: Number.isFinite(count) ? count : undefined,
    });
  }
  return out;
}

function parseFacetExtras(raw: unknown): SearchFacetExtras | null {
  const r = asRecord(raw);
  if (!r) {
    return null;
  }
  const u = r.unselectAllFacetsUrl;
  return {
    unselectAllFacetsUrl:
      u === null || typeof u === 'string' ? (u as string | null) : null,
    hasSelectedNonTabFacets: Boolean(r.hasSelectedNonTabFacets),
    hasNonTabFacets: Boolean(r.hasNonTabFacets),
  };
}

function parseQuestionEcho(raw: unknown): SearchQuestionEcho | null {
  const q = asRecord(raw);
  if (!q) {
    return null;
  }
  const collection = asRecord(q.collection);
  const queryVal = q.query;
  const origVal = q.originalQuery;
  return {
    executionContext: asString(q.executionContext),
    query: queryVal === null || typeof queryVal === 'string' ? queryVal : null,
    originalQuery:
      origVal === null || typeof origVal === 'string' ? origVal : null,
    collectionId: collection ? asString(collection.id) : '',
    profile: asString(q.profile),
    locale: asString(q.locale),
  };
}

function funnelbackApiError(root: UnknownRecord): string | null {
  const err = root.error;
  if (err === null || err === undefined) {
    return null;
  }
  if (typeof err === 'string') {
    return err.length > 0 ? err : null;
  }
  return null;
}

export type ParsedFunnelbackSearch = {
  results: SearchResultItem[];
  totalResults: number;
  facetMembers: Record<string, SearchFacetMember[]>;
  funnelbackFacets: unknown[];
  facetExtras: SearchFacetExtras | null;
  questionEcho: SearchQuestionEcho | null;
  returnCode: number | null;
  funnelbackError: string | null;
};

/**
 * Maps Funnelback JSON++ search responses to Deakin Life listing UI state.
 */
export function parseFunnelbackSearchResponse(
  raw: unknown,
): ParsedFunnelbackSearch {
  const empty: ParsedFunnelbackSearch = {
    results: [],
    totalResults: 0,
    facetMembers: {},
    funnelbackFacets: [],
    facetExtras: null,
    questionEcho: null,
    returnCode: null,
    funnelbackError: null,
  };
  const root = asRecord(raw);
  if (!root) {
    return empty;
  }

  const apiErr = funnelbackApiError(root);
  if (apiErr) {
    return { ...empty, funnelbackError: apiErr };
  }

  const response = asRecord(root.response) ?? root;
  const rawPacket = response.resultPacket;
  const packet =
    rawPacket !== null && rawPacket !== undefined ? asRecord(rawPacket) : null;

  let totalResults = 0;
  const summary = packet ? asRecord(packet.resultsSummary) : null;
  if (summary) {
    const t = summary.totalMatching ?? summary.totalResults;
    if (typeof t === 'number') {
      totalResults = t;
    } else if (typeof t === 'string') {
      totalResults = Number.parseInt(t, 10) || 0;
    }
  }

  const rawResults = packet?.results;
  const results: SearchResultItem[] = [];
  if (Array.isArray(rawResults)) {
    for (const r of rawResults) {
      const item = mapResultRow(r);
      if (item) {
        results.push(item);
      }
    }
  }

  const rawFacets = response.facets;
  const funnelbackFacets = Array.isArray(rawFacets) ? [...rawFacets] : [];

  const facetMembers: Record<string, SearchFacetMember[]> = {};
  if (Array.isArray(rawFacets)) {
    for (const facet of rawFacets) {
      const f = asRecord(facet);
      if (!f) continue;
      const name = asString(f.name ?? f.guess ?? '');
      if (!name) continue;
      const categories = mapFacetCategories(
        f.categories ?? f.values ?? f.allValues,
      );
      if (categories.length > 0) {
        facetMembers[name] = categories;
      }
    }
  }

  const returnCodeRaw = response.returnCode;
  const returnCode =
    typeof returnCodeRaw === 'number' && Number.isFinite(returnCodeRaw)
      ? returnCodeRaw
      : null;

  return {
    results,
    totalResults,
    facetMembers,
    funnelbackFacets,
    facetExtras: parseFacetExtras(response.facetExtras),
    questionEcho: parseQuestionEcho(root.question),
    returnCode,
    funnelbackError: null,
  };
}
